import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArticleCard } from '../../components/minor.jsx';
import { fetchMultipleNpmStats } from '../../services/activityService.js';
import './Authors.css';

const Authors = ({ authors = [], news = [], announcements = [], achievements = {}, loading: dataLoading = false }) => {
  const { id } = useParams();
  const [author, setAuthor] = useState(null);
  const [authorArticles, setAuthorArticles] = useState([]);
  const [authorAchievements, setAuthorAchievements] = useState({});
  const [npmStats, setNpmStats] = useState(null);
  const [activeSection, setActiveSection] = useState('bio'); // Zmiana na 'bio' jako domyślna
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dataLoading && authors.length > 0) {
      // Szukaj autora po nickname lub id
      const foundAuthor = authors.find(a => 
        a.nickname.toLowerCase() === id.toLowerCase() || a.id === parseInt(id)
      );
      
      if (foundAuthor) {
        setAuthor(foundAuthor);
        
        // Pobierz artykuły autora - sprawdź zarówno authorId jak i author
        const articles = [...news, ...announcements].filter(item => 
          item.authorId === foundAuthor.id || item.author === foundAuthor.nickname
          );
        setAuthorArticles(articles);
        
        // Pobierz osiągnięcia autora - użyj globalnych achievements
        setAuthorAchievements(achievements);
        
        // Pobierz statystyki NPM
        fetchMultipleNpmStats().then(stats => {
          setNpmStats(stats);
        }).catch(error => {
          console.error('Error fetching NPM stats:', error);
        });
      }
      setLoading(false);
    }
    if (!dataLoading && authors.length === 0) {
      setAuthor(null);
      setAuthorArticles([]);
      setAuthorAchievements(achievements);
      setLoading(false);
    }
  }, [id, dataLoading, authors, news, announcements, achievements]);

  // Usuwamy scroll-based detection, pozostaje tylko klikane przełączanie
  // useEffect(() => {
  //   const handleScroll = () => {
  //     const articlesSection = document.getElementById('articles-section');
  //     const achievementsSection = document.getElementById('achievements-section');
  //     if (articlesSection && achievementsSection) {
  //       const scrollPosition = window.scrollY + 200;
  //       const articlesTop = articlesSection.offsetTop;
  //       const achievementsTop = achievementsSection.offsetTop;
  //       if (scrollPosition >= achievementsTop) setActiveSection('achievements');
  //       else if (scrollPosition >= articlesTop) setActiveSection('articles');
  //     }
  //   };
  //   window.addEventListener('scroll', handleScroll);
  //   handleScroll();
  //   return () => window.removeEventListener('scroll', handleScroll);
  // }, [author]);

  // eslint-disable-next-line no-unused-vars
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // eslint-disable-next-line no-unused-vars
  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const calculateProgress = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  // Funkcja do przełączania zakładek
  const switchTab = (tabName) => {
    setActiveSection(tabName);
  };

  if (loading) {
    return (
      <div className="author-profile-container">
        <div className="container">
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <div style={{ fontSize: '1.5rem', color: '#666' }}>Ładowanie...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!author) {
    return (
      <div className="author-profile-container">
        <div className="container">
          <div className="author-not-found">
            <h1 className="author-not-found-title">404</h1>
            <p className="author-not-found-message">
              Nie znaleziono autora o nazwie "{id}"
            </p>
            <Link to="/" className="author-back-button">
              Powrót do strony głównej
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="author-profile-container">
      <div className="author-profile-wrapper">
        {/* Karta informacji o autorze */}
        <div className="author-info-card">
          <img 
            src={`/images/${author.profilePic}`} 
            alt={author.nickname}
            className="author-profile-pic"
            onError={(e) => {
              e.target.src = '/images/default-avatar.png';
            }}
          />
          <h1 className="author-nickname">{author.nickname}</h1>
          <p className="author-bio">{author.bio}</p>
          
          {/* Sekcja specjalizacji */}
          {author.specialties && (
            <div className="author-specialties">
              <h3>Specjalizacje</h3>
              <div className="author-specialties-list">
                {author.specialties.map((specialty, index) => (
                  <span key={index} className="author-specialty-tag">
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Sekcja osiągnięć - odznaki */}
          <div className="author-achievements-badges">
            <h3>Osiągnięcia</h3>
            <div className="achievements-badges-grid">
              {/* NPM Downloads - dynamiczne dane */}
              <div className="achievement-badge active">
                <div className="badge-icon">📦</div>
                <div className="badge-info">
                  <div className="badge-title">NPM Downloads</div>
                  <div className="badge-progress">
                    {npmStats && npmStats.length > 0 ? 
                      `${npmStats.reduce((total, pkg) => total + pkg.totalDownloads, 0).toLocaleString()} pobrań` : 
                      'Ładowanie...'
                    }
                  </div>
                </div>
              </div>
              
              {/* QuickBlocksUI Package */}
              {npmStats && npmStats.find(pkg => pkg.package.includes('quickblocksui')) && (
                <div className="achievement-badge active">
                  <div className="badge-icon">🧩</div>
                  <div className="badge-info">
                    <div className="badge-title">QuickBlocksUI</div>
                    <div className="badge-progress">
                      {npmStats && npmStats.length > 0 ? 
                        `${npmStats.find(pkg => pkg.package.includes('quickblocksui'))?.totalDownloads?.toLocaleString() || '0'} pobrań` : 
                        'Ładowanie...'
                      }
                    </div>
                  </div>
                </div>
              )}
              
              {/* React Components Package */}
              {npmStats && npmStats.find(pkg => pkg.package.includes('react.js-components')) && (
                <div className="achievement-badge active">
                  <div className="badge-icon">⚛️</div>
                  <div className="badge-info">
                    <div className="badge-title">React Components</div>
                    <div className="badge-progress">
                      {npmStats && npmStats.length > 0 ? 
                        `${npmStats.find(pkg => pkg.package.includes('react.js-components'))?.totalDownloads?.toLocaleString() || '0'} pobrań` : 
                        'Ładowanie...'
                      }
                    </div>
                  </div>
                </div>
              )}
              
              {/* Zablokowane osiągnięcia */}
              <div className="achievement-badge locked">
                <div className="badge-icon">🚀</div>
                <div className="badge-info">
                  <div className="badge-title">Rocket Launch</div>
                  <div className="badge-progress">Zablokowane</div>
                </div>
              </div>
              
              <div className="achievement-badge locked">
                <div className="badge-icon">⭐</div>
                <div className="badge-info">
                  <div className="badge-title">Star Collector</div>
                  <div className="badge-progress">Zablokowane</div>
                </div>
              </div>
              
              <div className="achievement-badge locked">
                <div className="badge-icon">🎯</div>
                <div className="badge-info">
                  <div className="badge-title">Target Master</div>
                  <div className="badge-progress">Zablokowane</div>
                </div>
              </div>
            </div>
          </div>

          {/* Sekcja mediów społecznościowych - ikony */}
          {author.socialMedia && (
            <div className="author-social-media">
              <h3>Kontakt</h3>
              <div className="social-icons">
                {author.socialMedia.github && (
                  <a href={author.socialMedia.github} target="_blank" rel="noopener noreferrer" className="social-icon github" title="GitHub">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                    </svg>
                  </a>
                )}
                {author.socialMedia.youtube && (
                  <a href={author.socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="social-icon youtube" title="YouTube">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                )}
                {author.socialMedia.twitter && (
                  <a href={author.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="social-icon twitter" title="Twitter">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                )}
                {author.socialMedia.discord && (
                  <div className="social-icon discord" title={`Discord: ${author.socialMedia.discord}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0189 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
                    </svg>
                  </div>
                )}
                {author.socialMedia.instagram && (
                  <a href={author.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="social-icon instagram" title="Instagram">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                )}
                {author.socialMedia.linkedin && (
                  <a href={author.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="social-icon linkedin" title="LinkedIn">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                )}
              </div>
            </div>
          )}
          
          <div className="author-stats">
            <div className="author-stat-item">
              <span className="author-stat-label">Artykuły</span>
              <span className="author-stat-value">{authorArticles.length}</span>
            </div>
            <div className="author-stat-item">
              <span className="author-stat-label">ID Autora</span>
              <span className="author-stat-value">#{author.id}</span>
            </div>
            <div className="author-stat-item">
              <span className="author-stat-label">Dołączył</span>
              <span className="author-stat-value">
                {author.joinDate ? new Date(author.joinDate).getFullYear() : '2024'}
              </span>
            </div>
          </div>
        </div>

        {/* Sekcja treści autora */}
        <div className="author-content-section">
          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button 
              className={`tab-item ${activeSection === 'bio' ? 'active' : ''}`}
              onClick={() => switchTab('bio')}
            >
              <span className="tab-text">Bio</span>
            </button>
            <button 
              className={`tab-item ${activeSection === 'articles' ? 'active' : ''}`}
              onClick={() => switchTab('articles')}
            >
              <span className="tab-text">Artykuły</span>
              <span className="tab-count">{authorArticles.length}</span>
            </button>
            <button 
              className={`tab-item ${activeSection === 'achievements' ? 'active' : ''}`}
              onClick={() => switchTab('achievements')}
            >
              <span className="tab-text">Dokonania</span>
              <span className="tab-count">{Object.keys(authorAchievements).length}</span>
            </button>
          </div>

          {/* Zawartość zakładek */}
          <div className="tab-content">
            {/* Sekcja Bio */}
            {activeSection === 'bio' && (
              <section id="bio-section" className="bio-section">
                <h2 className="section-title">O autorze</h2>
                <div className="bio-content">
                  <div className="bio-description">
                    <p>{author.bio}</p>
                  </div>
                  <div className="featured-achievements">
                    <h3 className="featured-title">Najważniejsze osiągnięcia</h3>
                    <div className="featured-achievements-grid">
                      {Object.entries(authorAchievements).slice(0, 3).map(([key, achievement]) => {
                        const progress = achievement.milestones?.find(m => !m.achieved) 
                          ? calculateProgress(achievement.current, achievement.milestones.find(m => !m.achieved).value)
                          : 100;
                        return (
                          <div key={key} className="featured-achievement-card">
                            <h4 className="featured-achievement-title">{achievement.title}</h4>
                            <div className="featured-achievement-current">
                              <span className="featured-number">{achievement.current.toLocaleString()}</span>
                              <span className="featured-unit">
                                {key === 'npm' ? 'pobrań' : key === 'gamedev' ? 'obserwujących' : 'gwiazdek'}
                              </span>
                            </div>
                            <div className="featured-progress">
                              <div className="featured-progress-bar" style={{ width: `${progress}%` }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Sekcja Artykułów */}
            {activeSection === 'articles' && (
              <section id="articles-section" className="articles-section">
                <h2 className="section-title">Artykuły</h2>
                {authorArticles.length > 0 ? (
                  <div className="author-articles-grid compact">
                    {authorArticles.slice(0, 6).map((article, index) => (
                      <ArticleCard 
                        key={article.id}
                        article={article} 
                        index={index}
                        authors={authors}
                        compact={true}
                      />
                    ))}
                    {authorArticles.length > 6 && (
                      <div className="show-more-articles">
                        <span>+{authorArticles.length - 6} więcej artykułów</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">
                    Ten autor nie opublikował jeszcze żadnych artykułów.
                  </div>
                )}
              </section>
            )}

            {/* Sekcja Osiągnięć */}
            {activeSection === 'achievements' && (
              <section id="achievements-section" className="achievements-section">
                <h2 className="section-title">Wszystkie dokonania</h2>
                <div className="author-achievements-grid">
                  {Object.entries(authorAchievements).map(([key, achievement]) => {
                    const nextMilestone = achievement.milestones?.find(m => !m.achieved);
                    const progress = nextMilestone 
                      ? calculateProgress(achievement.current, nextMilestone.value)
                      : 100;
                    return (
                      <div key={key} className="author-achievement-card">
                        <div className="achievement-header">
                          <h3 className="author-achievement-title">{achievement.title}</h3>
                        </div>
                        <div className="achievement-current">
                          <span className="achievement-number">{achievement.current.toLocaleString()}</span>
                        </div>
                        <div className="achievement-progress">
                          <div className="achievement-progress-bar" style={{ width: `${progress}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Authors;
